/**
 * v3.18-1320 — Operational Assignments & Company Memberships panel.
 *
 * Drop this component into any employee edit modal.  It renders two
 * independent sections:
 *
 *   • Company Memberships — primary company (read-only summary) + a list
 *     of additional company memberships with Add / Remove.
 *   • Operational Assignments — multi-select against the live catalog
 *     (`GET /api/employees/assignment-catalog`).  VO team entries are
 *     driven by the real ``is_vo=True`` users so new VOs immediately
 *     appear here.
 *
 * These two concepts are deliberately independent per operator directive:
 *   Company Membership  → WHAT companies an employee belongs to.
 *   Operational Assignment → WHAT they do inside those companies.
 *
 * Every mutation writes an immutable audit row on the backend.
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Plus, X, Building2, Layers } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

function safeErr(e, fallback) {
  return String(e?.response?.data?.detail || fallback || 'Request failed.');
}

export default function EmployeeAssignmentsPanel({ userId }) {
  const [loading, setLoading]           = useState(false);
  const [memberships, setMemberships]   = useState(null);
  const [assignments, setAssignments]   = useState([]);
  const [catalog, setCatalog]           = useState([]);
  const [companies, setCompanies]       = useState([]);
  const [busy, setBusy]                 = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [ms, as, cat, cos] = await Promise.all([
        api.get(`/employees/${userId}/memberships`),
        api.get(`/employees/${userId}/assignments`),
        api.get('/employees/assignment-catalog'),
        api.get('/companies'),
      ]);
      setMemberships(ms.data);
      setAssignments(as.data?.items || []);
      setCatalog(cat.data?.catalog || []);
      setCompanies(Array.isArray(cos.data) ? cos.data
                   : (cos.data?.items || cos.data?.companies || []));
    } catch (e) {
      toast.error(safeErr(e, 'Could not load employee assignments.'));
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { if (userId) reload(); }, [userId, reload]);

  // Precompute which companies are still selectable for Add Company.
  const currentCompanyIds = useMemo(() => new Set([
    memberships?.primary?.company_id,
    ...(memberships?.additional || []).map(a => a.company_id),
  ].filter(Boolean)), [memberships]);
  const currentAssignmentKeys = useMemo(() =>
    new Set(assignments.map(a => a.key)), [assignments]);

  // v3.18-1321 — DYNAMIC categorisation from the Team catalog.
  // Category order: Coverage first, then anything the Team collection
  // reports (Sales, VO, Concierge, Funding, Applications, Management,
  // future types).  Each Team's team_type IS the authoritative category.
  const CATEGORY_LABELS = {
    coverage:     'Coverage',
    sales:        'Sales Teams',
    vo:           'VO Teams',
    concierge:    'Concierge Teams',
    funding:      'Funding Teams',
    applications: 'Applications Teams',
    management:   'Management Teams',
  };
  const categories = useMemo(() => {
    const buckets = {};
    for (const c of catalog) {
      const cat = c.category || 'other';
      if (!buckets[cat]) buckets[cat] = [];
      buckets[cat].push(c);
    }
    const order = ['coverage', 'sales', 'vo', 'concierge', 'funding',
                   'applications', 'management'];
    const seen  = new Set();
    const out   = [];
    for (const k of order) {
      if (buckets[k]) { out.push({ key: k,
                                   label: CATEGORY_LABELS[k] || k.replace(/^\w/, c=>c.toUpperCase()),
                                   entries: buckets[k] });
                         seen.add(k); }
    }
    for (const k of Object.keys(buckets)) {
      if (!seen.has(k)) out.push({ key: k,
                                   label: CATEGORY_LABELS[k] || k.replace(/^\w/, c=>c.toUpperCase()) + ' Teams',
                                   entries: buckets[k] });
    }
    return out;
  }, [catalog]);

  const addMembership = async (company_id) => {
    setBusy(true);
    try {
      await api.post(`/employees/${userId}/memberships`, { company_id });
      toast.success('Company membership added.');
      reload();
    } catch (e) { toast.error(safeErr(e)); }
    finally { setBusy(false); }
  };
  const removeMembership = async (company_id) => {
    if (!window.confirm('Remove this company membership? This is audited.')) return;
    setBusy(true);
    try {
      await api.delete(`/employees/${userId}/memberships/${company_id}`);
      toast.success('Company membership removed.');
      reload();
    } catch (e) { toast.error(safeErr(e)); }
    finally { setBusy(false); }
  };
  const toggleAssignment = async (a) => {
    setBusy(true);
    try {
      if (currentAssignmentKeys.has(a.key)) {
        await api.delete(`/employees/${userId}/assignments/${encodeURIComponent(a.key)}`);
        toast.success('Assignment removed.');
      } else {
        await api.post(`/employees/${userId}/assignments`, { key: a.key });
        toast.success('Assignment added.');
      }
      reload();
    } catch (e) { toast.error(safeErr(e)); }
    finally { setBusy(false); }
  };

  if (loading && !memberships) {
    return <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/> Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="employee-assignments-panel">
      {/* Company Memberships */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">Company Memberships</h3>
        </div>
        <div className="rounded border p-3 space-y-2">
          <div className="text-xs" data-testid="employee-primary-company">
            <span className="uppercase tracking-wider text-slate-400 mr-2">Primary</span>
            <span className="font-semibold text-slate-800">
              {memberships?.primary?.name || '—'}
            </span>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Additional Companies</div>
          {(memberships?.additional || []).length === 0 ? (
            <div className="text-xs italic text-slate-500 py-1">No additional companies.</div>
          ) : (
            <div className="space-y-1">
              {memberships.additional.map(m => (
                <div key={m.company_id}
                     className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1"
                     data-testid={`employee-additional-company-${m.company_id}`}>
                  <span className="font-medium text-slate-700">{m.name}</span>
                  <button
                    onClick={() => removeMembership(m.company_id)}
                    disabled={busy}
                    className="text-slate-400 hover:text-red-600"
                    title="Remove company">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <select
              className="text-xs border rounded px-2 py-1 flex-1"
              value=""
              disabled={busy}
              onChange={e => { if (e.target.value) addMembership(e.target.value); }}
              data-testid="employee-add-membership-select">
              <option value="">+ Add company membership…</option>
              {companies.filter(c => !currentCompanyIds.has(c.id)).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Operational Assignments — categories are DYNAMIC from the Team
          catalog (v3.18-1321).  No hard-coded VO Team rendering: creating
          a new Team of any team_type simply grows the corresponding
          category. */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Layers className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">Operational Assignments</h3>
        </div>
        <div className="rounded border p-3 space-y-3">
          {categories.map(({ key: catKey, label, entries }) => (
            <div key={catKey}>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 pt-1">{label}</div>
              <div className="flex flex-wrap gap-2 mt-1">
                {entries.map(a => {
                  const on = currentAssignmentKeys.has(a.key);
                  const tone = catKey === 'coverage' ? 'emerald'
                             : catKey === 'vo'       ? 'indigo'
                             : catKey === 'sales'    ? 'blue'
                             : catKey === 'funding'  ? 'amber'
                             : catKey === 'applications' ? 'violet'
                             : catKey === 'concierge' ? 'teal'
                             : 'slate';
                  const activeCls =
                    tone === 'emerald' ? 'bg-emerald-600 text-white border-emerald-700'
                  : tone === 'indigo'  ? 'bg-indigo-600 text-white border-indigo-700'
                  : tone === 'blue'    ? 'bg-blue-600 text-white border-blue-700'
                  : tone === 'amber'   ? 'bg-amber-600 text-white border-amber-700'
                  : tone === 'violet'  ? 'bg-violet-600 text-white border-violet-700'
                  : tone === 'teal'    ? 'bg-teal-600 text-white border-teal-700'
                  :                      'bg-slate-600 text-white border-slate-700';
                  return (
                    <button
                      key={a.key}
                      onClick={() => toggleAssignment(a)}
                      disabled={busy}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        on ? activeCls
                           : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`employee-assign-${a.key.replace(':','-')}`}>
                      {on ? '✓ ' : '+ '}{a.label}
                    </button>
                  );
                })}
                {entries.length === 0 && (
                  <div className="text-xs italic text-slate-500">No {label.toLowerCase()} assignments configured yet.</div>
                )}
              </div>
            </div>
          ))}

          {/* Selected assignments summary */}
          {assignments.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Currently Assigned</div>
              <div className="flex flex-wrap gap-1">
                {assignments.map(a => (
                  <Badge key={a.key} variant="outline" className="text-[10px]">
                    {a.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
